import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, MessageSquare, X, Monitor, Maximize } from 'lucide-react';
import { slides, talkingPoints } from '../data/webinarContent';
import TitleSlide from './slides/TitleSlide';
import AgendaSlide from './slides/AgendaSlide';
import ProblemSlide from './slides/ProblemSlide';
import SolutionSlide from './slides/SolutionSlide';
import CaseStudySlide from './slides/CaseStudySlide';
import InteractiveScenarioSlide from './slides/InteractiveScenarioSlide';
import SuccessStoriesSlide from './slides/SuccessStoriesSlide';
import ImplementationSlide from './slides/ImplementationSlide';
import CTASlide from './slides/CTASlide';
import QASlide from './slides/QASlide';
import TalkingPoints from './TalkingPoints';
export function WebinarPresentation() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [presenterMode, setPresenterMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    // Initialize BroadcastChannel for presenter mode sync
    const channel = new BroadcastChannel('webinar-sync');
    // Listen for slide changes from presenter notes window
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'SLIDE_CHANGE') {
        console.log('Main window received slide change:', event.data.slideIndex);
        setCurrentSlide(event.data.slideIndex);
      }
    };
    channel.addEventListener('message', handleMessage);
    // Get initial slide from localStorage
    const savedSlide = localStorage.getItem('currentSlide');
    if (savedSlide) {
      const slideNum = parseInt(savedSlide);
      console.log('Loading saved slide:', slideNum);
      setCurrentSlide(slideNum);
    }
    // Listen for fullscreen changes
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && currentSlide < slides.length - 1) {
        changeSlide(currentSlide + 1);
      } else if (e.key === 'ArrowLeft' && currentSlide > 0) {
        changeSlide(currentSlide - 1);
      } else if (e.key === 'n' || e.key === 'N') {
        setShowNotes(!showNotes);
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, showNotes]);
  const changeSlide = (newSlide: number) => {
    console.log('Changing slide to:', newSlide);
    setCurrentSlide(newSlide);
    // Save to localStorage
    localStorage.setItem('currentSlide', newSlide.toString());
    // Broadcast to presenter notes window
    const channel = new BroadcastChannel('webinar-sync');
    channel.postMessage({
      type: 'SLIDE_CHANGE',
      slideIndex: newSlide
    });
    console.log('Broadcasted slide change:', newSlide);
    channel.close();
  };
  const openPresenterMode = () => {
    // Save current slide before opening
    localStorage.setItem('currentSlide', currentSlide.toString());
    // Open presenter notes in new window
    const baseUrl = window.location.origin + window.location.pathname;
    const notesUrl = baseUrl + '?view=presenter';
    const notesWindow = window.open(notesUrl, 'presenter-notes', 'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no');
    if (notesWindow) {
      setPresenterMode(true);
      setShowNotes(false);
      console.log('Opened presenter notes window');
    } else {
      alert('Please allow pop-ups to use Presenter Mode');
    }
  };
  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } catch (err) {
        console.error('Error entering fullscreen:', err);
      }
    } else {
      try {
        await document.exitFullscreen();
        setIsFullscreen(false);
      } catch (err) {
        console.error('Error exiting fullscreen:', err);
      }
    }
  };
  const renderSlide = () => {
    const slide = slides[currentSlide];
    switch (slide.type) {
      case 'title':
        return <TitleSlide content={slide.content} data-id="element-244" />;
      case 'agenda':
        return <AgendaSlide content={slide.content} data-id="element-245" />;
      case 'problem':
        return <ProblemSlide content={slide.content} data-id="element-246" />;
      case 'solution':
        return <SolutionSlide content={slide.content} data-id="element-247" />;
      case 'case-study':
        return <CaseStudySlide content={slide.content} data-id="element-248" />;
      case 'interactive-scenario':
        return <InteractiveScenarioSlide content={slide.content} data-id="element-249" />;
      case 'success-stories':
        return <SuccessStoriesSlide content={slide.content} data-id="element-250" />;
      case 'implementation':
        return <ImplementationSlide content={slide.content} data-id="element-251" />;
      case 'cta':
        return <CTASlide content={slide.content} data-id="element-252" />;
      case 'qa':
        return <QASlide data-id="element-253" />;
      default:
        return <div data-id="element-254">Slide not found</div>;
    }
  };
  const currentTalkingPoints = talkingPoints.find(tp => tp.slideId === slides[currentSlide].id);
  return <div className="w-full h-screen bg-[#1C1C1C] flex flex-col" data-id="element-255">
      {/* Header */}
      <div className="bg-[#1C1C1C] border-b border-[#4D4D4D] px-6 py-4 flex items-center justify-between" data-id="element-256">
        <div className="flex items-center gap-4" data-id="element-257">
          <div className="text-[#0FB07D] font-bold text-xl" data-id="element-258">VIRIDIA</div>
          <div className="text-[#F5F5F5] text-sm" data-id="element-259">
            AI Response Strategies Webinar
          </div>
        </div>
        <div className="flex items-center gap-4" data-id="element-260">
          <div className="text-[#4D4D4D] text-sm" data-id="element-261">
            Slide {currentSlide + 1} / {slides.length}
          </div>

          {/* Fullscreen Button */}
          <button onClick={toggleFullscreen} className="flex items-center gap-2 px-4 py-2 bg-[#4D4D4D] text-white rounded-lg hover:bg-[#017C6D] transition-colors" title="Toggle fullscreen (F key)" data-id="element-262">
            <Maximize size={16} data-id="element-263" />
            {isFullscreen ? 'Exit' : 'Fullscreen'}
          </button>

          {/* Presenter Mode Button */}
          <button onClick={openPresenterMode} className="flex items-center gap-2 px-4 py-2 bg-[#0FB07D] text-white rounded-lg hover:bg-[#017C6D] transition-colors" title="Open presenter notes in separate window" data-id="element-264">
            <Monitor size={16} data-id="element-265" />
            Presenter Mode
          </button>

          {/* Toggle Notes Button (only show if not in presenter mode) */}
          {!presenterMode && <button onClick={() => setShowNotes(!showNotes)} className="flex items-center gap-2 px-4 py-2 bg-[#017C6D] text-white rounded-lg hover:bg-[#0FB07D] transition-colors" data-id="element-266">
              <MessageSquare size={16} data-id="element-267" />
              {showNotes ? 'Hide' : 'Show'} Notes
            </button>}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden" data-id="element-268">
        {/* Slide Area */}
        <div className="flex-1 flex items-center justify-center p-8 overflow-auto" data-id="element-269">
          <div className="w-full max-w-6xl" data-id="element-270">{renderSlide()}</div>
        </div>

        {/* Talking Points Sidebar (only show if not in presenter mode) */}
        {showNotes && !presenterMode && currentTalkingPoints && <div className="w-96 bg-[#1C1C1C] border-l border-[#4D4D4D] overflow-auto" data-id="element-271">
            <div className="p-6" data-id="element-272">
              <div className="flex items-center justify-between mb-4" data-id="element-273">
                <h3 className="text-[#F5F5F5] font-semibold" data-id="element-274">Speaker Notes</h3>
                <button onClick={() => setShowNotes(false)} className="text-[#4D4D4D] hover:text-[#F5F5F5]" data-id="element-275">
                  <X size={20} data-id="element-276" />
                </button>
              </div>
              <TalkingPoints talkingPoint={currentTalkingPoints} data-id="element-277" />
            </div>
          </div>}
      </div>

      {/* Navigation Footer */}
      <div className="bg-[#1C1C1C] border-t border-[#4D4D4D] px-6 py-4 flex items-center justify-between" data-id="element-278">
        <button onClick={() => changeSlide(Math.max(0, currentSlide - 1))} disabled={currentSlide === 0} className="flex items-center gap-2 px-4 py-2 bg-[#4D4D4D] text-white rounded-lg hover:bg-[#017C6D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-279">
          <ChevronLeft size={20} data-id="element-280" />
          Previous
        </button>

        {/* Progress Bar */}
        <div className="flex-1 mx-8" data-id="element-281">
          <div className="h-2 bg-[#4D4D4D] rounded-full overflow-hidden" data-id="element-282">
            <div className="h-full bg-[#0FB07D] transition-all duration-300" style={{
            width: `${(currentSlide + 1) / slides.length * 100}%`
          }} data-id="element-283" />
          </div>
        </div>

        <button onClick={() => changeSlide(Math.min(slides.length - 1, currentSlide + 1))} disabled={currentSlide === slides.length - 1} className="flex items-center gap-2 px-4 py-2 bg-[#0FB07D] text-white rounded-lg hover:bg-[#017C6D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-284">
          Next
          <ChevronRight size={20} data-id="element-285" />
        </button>
      </div>

      {/* Keyboard Shortcuts Hint */}
      <div className="absolute bottom-20 left-6 text-[#4D4D4D] text-xs" data-id="element-286">
        ← → Navigate • N Notes • F Fullscreen • Presenter Mode for separate
        notes window
      </div>
    </div>;
}